import React, { useState } from "react";
import { Download, Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { getAttribution } from "../lib/attribution";
import { publicApi, mediaUrl } from "../lib/api";
import { trackEvent } from "../lib/analytics";
import { useToast } from "../hooks/use-toast";
import MarketingConsent from "./MarketingConsent";
import LeadSourceField from "./LeadSourceField";
import { emptyLeadSource, leadSourceError, leadSourcePayload } from "../lib/leadSource";

export default function GuideDownloadModal({ guide, open, onClose }) {
  const { toast } = useToast();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [consent, setConsent] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [leadSource, setLeadSource] = useState(emptyLeadSource);
  const [sourceOptions, setSourceOptions] = useState([]);
  const [sourceError, setSourceError] = useState("");

  const reset = () => { setName(""); setEmail(""); setConsent(false); setSubmitting(false); setLeadSource(emptyLeadSource); setSourceError(""); };
  const handleClose = () => { reset(); onClose(); };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!guide) return;
    // Optional here, to keep a free download friction-free. Only the follow-up
    // box on "Other" has to be filled in once they have chosen something.
    const msg = leadSourceError(leadSource, sourceOptions, false);
    setSourceError(msg);
    if (msg) return;
    setSubmitting(true);
    try {
      const res = await publicApi.downloadGuide(guide.id, { name, email, marketing_consent: consent, attribution: getAttribution(), lead_source: leadSourcePayload(leadSource) });
      const url = mediaUrl(res.file_url);
      trackEvent("file_download", {
        file_name: guide?.title || guide?.id,
        file_extension: "pdf",
        link_url: guide?.slug || guide?.id,
      }, "guide_download");
      window.open(url, "_blank", "noopener,noreferrer");
      toast({ title: "Enjoy your guide!", description: "Your download should begin in a new tab." });
      handleClose();
    } catch (err) {
      const msg = err?.response?.status === 429
        ? "Too many requests. Please try again shortly."
        : (err?.response?.data?.detail || "Something went wrong. Please try again.");
      toast({ title: "Couldn't get the guide", description: msg, variant: "destructive" });
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && handleClose()}>
      <DialogContent className="sm:max-w-md" data-testid="guide-download-modal">
        <DialogHeader>
          <DialogTitle className="font-display text-navy text-2xl">Get your free guide</DialogTitle>
          <DialogDescription className="text-slate-500">
            {guide?.title ? `Enter your details and we'll open "${guide.title}" for you.` : "Enter your details to download."}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4 mt-2" data-testid="guide-download-form">
          <div>
            <Label htmlFor="guide-name" className="text-navy font-semibold">Full Name</Label>
            <Input id="guide-name" data-testid="guide-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Jane Smith" className="mt-1.5" />
          </div>
          <div>
            <Label htmlFor="guide-email" className="text-navy font-semibold">Email Address *</Label>
            <Input id="guide-email" data-testid="guide-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="jane@company.co.uk" className="mt-1.5" />
          </div>
          <p className="text-xs text-slate-400">We'll only use your details to send you occasional helpful resources. No spam, unsubscribe anytime.</p>
          <LeadSourceField
            value={leadSource}
            onChange={(v) => { setLeadSource(v); setSourceError(""); }}
            onOptions={setSourceOptions}
            error={sourceError}
            testId="guide-lead-source"
          />
          <MarketingConsent checked={consent} onChange={setConsent} testId="guide-marketing-consent" />
          <Button type="submit" disabled={submitting} data-testid="guide-download-submit" className="w-full bg-orange hover:bg-orange-dark text-white font-bold rounded-full py-6">
            {submitting ? <><Loader2 size={16} className="mr-2 animate-spin" /> Preparing...</> : <>Download Guide <Download size={16} className="ml-2" /></>}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
